import React, { useState, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Import the date picker CSS

// Helper to format date as yyyy-mm-dd (for backend)
const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Helper to format date as dd-mm-yyyy (for display)
const formatDateToDDMMYYYY = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

// Helper to parse yyyy-mm-dd string to Date object
const parseYYYYMMDDToDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-");
    if (!year || !month || !day) return null;
    return new Date(Number(year), Number(month) - 1, Number(day));
};

const CustomDatePicker = ({
    label = "",
    required = false,
    disabled = false,
    placeholder = "Select a date",
    name,
    value, // Accept value prop to show selected date
    onChange, // Optional onChange handler
    ...props
}) => {
    // If value is provided, use it as the selected date; otherwise, use local state
    const [localValue, setLocalValue] = useState(
        value ? (value instanceof Date ? value : parseYYYYMMDDToDate(value)) : null
    );

    // Sync localValue with value prop if it changes
    useEffect(() => {
        if (value) {
            setLocalValue(value instanceof Date ? value : parseYYYYMMDDToDate(value));
        } else {
            setLocalValue(null);
        }
    }, [value]);

    const handleDateChange = (date) => {
        setLocalValue(date);
        if (onChange) {
            // Return formatted date string in yyyy-mm-dd format
            onChange(formatDateToYYYYMMDD(date));
        }
    };

    // Custom input to show DD-MM-YYYY format
    const CustomInput = React.forwardRef(({ value, onClick, onChange }, ref) => (
        <input
            type="text"
            onClick={onClick}
            onChange={onChange}
            ref={ref}
            value={localValue ? formatDateToDDMMYYYY(localValue) : ""}
            placeholder={placeholder}
            disabled={disabled}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            readOnly
        />
    ));

    return (
        <div className="form-control flex flex-col gap-2 p-2 z-10">
            {/* Label */}
            {label && (
                <label
                    htmlFor={name}
                    className="font-medium flex items-center"
                >
                    {label}
                    {required && <span className="text-red-600 font-bold ml-1">*</span>}
                </label>
            )}

            {/* Date Picker */}
            <ReactDatePicker
                selected={localValue}
                onChange={handleDateChange}
                disabled={disabled}
                customInput={<CustomInput />}
                {...props}
            />
        </div>
    );
};

export default CustomDatePicker;
